/**
 * Cross-app sessionStorage key holding the handoff bounce counter. The vn-pwa handoff (navigating to
 * `/app`) and the dashboard's bounce-to-PWA (navigating to `/`) share this ONE key — they run on the
 * same delivery origin — so the `/app` ⇄ `/` loop is bounded no matter which side is about to hop
 * next. The dashboard keeps its own copy of this contract (key + window + cap); keep the two in sync.
 */
export const BOUNCE_COUNT_KEY = 'vn-pwa:bounce-count'

/**
 * How many hops are allowed inside one rolling window before the loop is declared stuck. A healthy
 * re-auth (401) or host rotation costs a single hop; only a genuinely non-converging loop — a
 * permanently blocked storage or a permanently broken API host — spends this many in seconds.
 */
export const MAX_BOUNCES = 3

/**
 * The rolling window, measured from the first hop. A settled app makes no hops for far longer than
 * this, so the counter resets itself on the next lone hop; a stuck loop spends all its hops within a
 * few seconds and trips well inside the window.
 */
export const BOUNCE_WINDOW_MS = 20_000

/** Whether a hop may proceed, or the loop has spent its budget and a fallback must be shown instead. */
export type BounceDecision = 'exhausted' | 'proceed'

interface BounceState {
  /** Hop count so far in the current window. */
  n: number
  /** Epoch millis of the window's first hop. */
  t: number
}

/** The minimal Storage surface the guard touches — lets tests inject an in-memory fake. */
type GuardStorage = Pick<Storage, 'getItem' | 'removeItem' | 'setItem'>

/**
 * Stand-in used when session storage is unreachable (a sandboxed in-app / WebView browser, private
 * mode). A store we cannot write is a store we cannot use to bound the loop, so its `setItem` throws
 * — {@link recordBounce} catches that and reads it as ALREADY exhausted, refusing to hop blindly
 * forever. This is the whole guard's shield against `window.sessionStorage` access throwing.
 */
const UNAVAILABLE_STORAGE: GuardStorage = {
  getItem: () => null,
  removeItem: () => undefined,
  setItem: () => {
    throw new Error('session storage unavailable')
  },
}

/**
 * Resolve the guard store, never throwing. In the target in-app/WebView browsers even *reading*
 * `window.sessionStorage` can raise a SecurityError, so both the injected value and the property
 * access are handled: tests inject a fake; production reads session storage, falling back to the
 * refuse-to-hop {@link UNAVAILABLE_STORAGE} if it is missing or its getter throws.
 */
export const resolveBounceStorage = (injected?: GuardStorage): GuardStorage => {
  if (injected) return injected
  try {
    return globalThis.sessionStorage ?? UNAVAILABLE_STORAGE
  } catch {
    return UNAVAILABLE_STORAGE
  }
}

/** Parse the persisted counter, tolerating any legacy/corrupt value by treating it as no window yet. */
const parseState = (raw: null | string): BounceState | undefined => {
  if (raw === null) return undefined
  try {
    const parsed = JSON.parse(raw) as Partial<BounceState>
    if (typeof parsed?.n === 'number' && typeof parsed?.t === 'number') return { n: parsed.n, t: parsed.t }
  } catch {
    // A malformed value is treated as a fresh window below.
  }
  return undefined
}

/**
 * Record one handoff/bounce hop and decide whether it may proceed. Over {@link MAX_BOUNCES} inside a
 * single {@link BOUNCE_WINDOW_MS} window — or any storage we cannot read/write — is `'exhausted'`:
 * stop hopping and show a fallback instead of spinning the `/app` ⇄ `/` loop forever. A hop after the
 * window has elapsed starts a fresh window, so healthy lone bounces never accumulate.
 */
export const recordBounce = (now: number, storage: GuardStorage = resolveBounceStorage()): BounceDecision => {
  try {
    const prev = parseState(storage.getItem(BOUNCE_COUNT_KEY))
    const withinWindow = prev !== undefined && now - prev.t <= BOUNCE_WINDOW_MS
    const next: BounceState = withinWindow ? { n: prev.n + 1, t: prev.t } : { n: 1, t: now }
    if (next.n > MAX_BOUNCES) return 'exhausted'
    storage.setItem(BOUNCE_COUNT_KEY, JSON.stringify(next))
    return 'proceed'
  } catch {
    return 'exhausted'
  }
}

/**
 * Clear the counter. Used by the exhausted-fallback's Retry so a deliberate user retry starts from a
 * clean window rather than an already-spent one that would re-trip on the first hop.
 */
export const clearBounceCount = (storage: GuardStorage = resolveBounceStorage()): void => {
  try {
    storage.removeItem(BOUNCE_COUNT_KEY)
  } catch {
    // Nothing to clear if the store is unreachable.
  }
}
